<template>
  <div v-if="currentClaim">
    <div class="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
      <ShowPageHeader backLabel="Sinistres" backRoute="/claims" :title="currentClaim.friendlyId">
        <template #badges>
          <div
            v-if="currentClaim.pausedAt || currentClaim.ticketReopeningDate"
            class="flex flex-col gap-1 rounded-lg p-2 text-xs text-gray-600 bg-orange-100"
          >
            <div v-if="currentClaim.pausedAt" class="flex items-center gap-1.5">
              <PauseIcon class="w-4 h-4 text-orange-500" />
              <span>Mis en pause le {{ currentClaim.pausedAt }}</span>
            </div>
            <div v-if="currentClaim.ticketReopeningDate" class="flex items-center gap-1.5">
              <BookOpenIcon class="w-4 h-4 text-green-600" />
              <span>Réouverture le {{ currentClaim.ticketReopeningDate }}</span>
            </div>
          </div>
          <div
            v-if="claimPickupScheduled && currentClaim.pickupAppointmentDate"
            class="flex items-center gap-1.5 rounded-lg p-2 text-xs text-gray-600 bg-blue-100"
          >
            <CalendarIcon class="w-4 h-4 text-blue-500" />
            <span>RDV de reprise le {{ currentClaim.pickupAppointmentDate }}</span>
          </div>
          <FieldSpan
            :class="[statusStyle(currentClaim.status)]"
            translation-prefix="claim.status"
            :value="currentClaim.status"
          />
          <FieldSpan
            :value="currentClaim.claimType"
            translation-prefix="claim.claim_type"
            style-to-add="bg-purple-100 text-purple-800"
          />
          <FieldSpan
            :class="[ticketStatusStyle(currentClaim.ticketStatus)]"
            translation-prefix="claim.ticket_status"
            :value="currentClaim.ticketStatus"
          />
          <div
            v-if="currentClaim.fraudSuspicion"
            class="flex items-center gap-1.5 rounded-lg p-2 text-xs font-semibold text-red-800 bg-red-100"
          >
            <ShieldExclamationIcon class="w-4 h-4 text-red-600" />
            <span>{{ t("fraud_suspicion_badge") }}</span>
          </div>
        </template>
        <template #actions>
          <AiAnalysisFlowButton v-if="declaration" :declaration="declaration" @open="openAiAnalysisFlow = true" />
          <ClaimChatButton v-if="declaration" @open="openClaimChat = true" />
          <ActionsMenu :label="t('actions_menu_button')">
            <ActionsMenuItem v-if="claimOpen && claimApprovalRequired" @click="openUpdateClaimTypeModal = true">
              {{ t("update_claim_type_button") }}
            </ActionsMenuItem>
            <ActionsMenuItem
              v-if="claimTicketStatusOpen() || claimTicketStatusPaused()"
              @click="openPauseClaimModal = true"
            >
              <span v-if="claimTicketStatusOpen()">{{ t("pause_claim_button") }}</span>
              <span v-else>{{ t("update_reopening_date_button") }}</span>
            </ActionsMenuItem>
            <ActionsMenuItem @click="openFraudSuspicionModal = true">
              <span v-if="currentClaim.fraudSuspicion">{{ t("unflag_fraud_suspicion_button") }}</span>
              <span v-else>{{ t("flag_fraud_suspicion_button") }}</span>
            </ActionsMenuItem>
            <ActionsMenuItem @click="exportClaimData">{{ t("export_claim_data_button") }}</ActionsMenuItem>
            <ActionsMenuItem v-if="claimOpen" @click="openClaimCancelModal = true">
              {{ t("cancel_claim_button") }}
            </ActionsMenuItem>
            <ActionsMenuItem v-if="claimRefused || claimCancelled" @click="reopenClaim">
              {{ t("reopen_claim_button") }}
            </ActionsMenuItem>
            <ActionsMenuItem v-if="claimOpen && !!currentClaim.handlingType" @click="resetHandlingType">
              {{ t("reset_handling_type_button") }}
            </ActionsMenuItem>
          </ActionsMenu>
        </template>
      </ShowPageHeader>

      <div v-if="anyRequiredAction">
        <RequiredAction :claim="currentClaim" :declaration="declaration" />
      </div>

      <ClaimWorkflowActions
        v-else
        @open-quotation-upload="openClaimQuotationUploadModal = true"
        @open-invoice-upload="openClaimInvoiceUploadModal = true"
        @open-iban-upload="openIbanUploadModal = true"
      />
    </div>
    <ClaimAlertsSummary v-if="claimOpen" class="mt-4" :claim="currentClaim" :customer="customer" />
    <div class="mt-4 flex flex-col lg:flex-row gap-3">
      <div class="flex-1 min-w-0">
        <div class="relative bg-white rounded-2xl shadow-md p-6">
          <ClaimDeclarationsList :claim="currentClaim" />
          <ClaimProductSummary v-if="claimProduct" :claim-product="claimProduct" class="mt-4" />
        </div>
      </div>

      <ClaimDetailsSidebar :claim="currentClaim" @add-note="openClaimUpdateEstalyManagerNotesModal = true" />
    </div>

    <ClaimDetailsTabContent
      :claim="currentClaim"
      :claim-quotations="claimQuotations"
      :claim-invoices="claimInvoices"
      @updated="fetchClaim"
    />

    <ClaimChatPanel
      v-if="declaration"
      :declaration-id="declaration.id"
      :claim-status="currentClaim.status"
      :open="openClaimChat"
      @close="openClaimChat = false"
    />

    <div v-if="claimOpen">
      <IbanUploadModal :open="openIbanUploadModal" @close="openIbanUploadModal = false" />
      <ClaimInvoiceUploadModal :open="openClaimInvoiceUploadModal" @close="onInvoiceModalClose" />
      <ClaimQuotationUploadModal :open="openClaimQuotationUploadModal" @close="onQuotationModalClose" />
      <PauseClaimModal
        v-if="openPauseClaimModal"
        :claim="currentClaim"
        @close="openPauseClaimModal = false"
        @confirm="pauseClaim"
      />
      <UpdateClaimTypeModal
        :open="openUpdateClaimTypeModal"
        :claim="currentClaim"
        @close="openUpdateClaimTypeModal = false"
      />
    </div>

    <ClaimCancelModal :open="openClaimCancelModal" @close="openClaimCancelModal = false" />
    <FraudSuspicionModal
      :open="openFraudSuspicionModal"
      :claim="currentClaim"
      @close="openFraudSuspicionModal = false"
    />
    <ClaimUpdateEstalyManagerNotesModal
      :open="openClaimUpdateEstalyManagerNotesModal"
      :claim="currentClaim"
      @close="openClaimUpdateEstalyManagerNotesModal = false"
      @updated="fetchClaim"
    />

    <AiAnalysisFlowModal
      v-if="declaration"
      :open="openAiAnalysisFlow"
      :claim="currentClaim"
      :declaration="declaration"
      :customer="customer"
      @close="openAiAnalysisFlow = false"
    />
  </div>
  <ClaimNotFound v-else />
</template>

<script>
import { useI18n } from "vue-i18n";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { PauseIcon, CalendarIcon } from "@heroicons/vue/outline";
import { BookOpenIcon, ShieldExclamationIcon } from "@heroicons/vue/solid";
import { ActionsMenu, ActionsMenuItem, ClaimProductSummary, FieldSpan, ShowPageHeader, formatPrice } from "@estaly/ui";

import ClaimNotFound from "@/components/claim/list/utils/ClaimNotFound.vue";
import ClaimDeclarationsList from "@/components/claim/ClaimDeclarationsList.vue";
import RequiredAction from "@/components/claim/details/RequiredAction.vue";
import ClaimWorkflowActions from "@/components/claim/details/ClaimWorkflowActions.vue";
import ClaimDetailsSidebar from "@/components/claim/details/ClaimDetailsSidebar.vue";
import ClaimDetailsTabContent from "@/components/claim/details/ClaimDetailsTabContent.vue";
import ClaimChatPanel from "@/components/claim/details/ClaimChatPanel.vue";
import ClaimChatButton from "@/components/claim/details/ClaimChatButton.vue";

import ClaimInvoiceUploadModal from "@/components/claim/details/information/action/invoices/InvoiceUploadModal.vue";
import ClaimQuotationUploadModal from "@/components/claim/details/information/action/quotations/QuotationUploadModal.vue";
import ClaimCancelModal from "@/components/claim/details/ClaimCancelModal.vue";
import FraudSuspicionModal from "@/components/claim/details/FraudSuspicionModal.vue";
import PauseClaimModal from "@/components/claim/PauseClaimModal.vue";
import UpdateClaimTypeModal from "@/components/claim/details/UpdateClaimTypeModal.vue";

import ClaimAlertsSummary from "@/components/claim/ClaimAlertsSummary.vue";
import ClaimUpdateEstalyManagerNotesModal from "@/components/claim/details/ClaimUpdateEstalyManagerNotesModal.vue";
import IbanUploadModal from "@/components/claim/details/action/IbanUploadModal.vue";
import AiAnalysisFlowButton from "@/components/claim/details/ai-analysis-flow/AiAnalysisFlowButton.vue";
import AiAnalysisFlowModal from "@/components/claim/details/ai-analysis-flow/AiAnalysisFlowModal.vue";

export default {
  components: {
    ClaimUpdateEstalyManagerNotesModal,
    ClaimAlertsSummary,
    ClaimProductSummary,
    PauseClaimModal,
    PauseIcon,
    BookOpenIcon,
    ShieldExclamationIcon,
    CalendarIcon,
    ShowPageHeader,
    FieldSpan,
    ActionsMenu,
    ActionsMenuItem,
    ClaimQuotationUploadModal,
    ClaimInvoiceUploadModal,
    RequiredAction,
    ClaimWorkflowActions,
    ClaimDetailsSidebar,
    ClaimDetailsTabContent,
    ClaimChatPanel,
    ClaimChatButton,
    ClaimNotFound,
    ClaimDeclarationsList,
    ClaimCancelModal,
    FraudSuspicionModal,
    UpdateClaimTypeModal,
    IbanUploadModal,
    AiAnalysisFlowButton,
    AiAnalysisFlowModal,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  computed: {
    ...mapGetters("claims", [
      "claimRefused",
      "claimCancelled",
      "claimApprovalRequired",
      "claimOpen",
      "anyRequiredAction",
      "claimPickupScheduled",
      "claimTicketStatusOpen",
      "claimTicketStatusPaused",
      "ticketStatusStyle",
      "statusStyle",
    ]),
    ...mapState("claims", ["currentClaim"]),
    ...mapState("claimDeclarations", { claimDeclarationsIndex: "index" }),

    declaration() {
      return this.claimDeclarationsIndex?.[0];
    },
    claimProduct() {
      return this.currentClaim?.claimProduct;
    },
    claimFriendlyId() {
      return this.$route.params.id;
    },
    customer() {
      return this.currentClaim?.customer;
    },
  },

  data: () => {
    return {
      openClaimInvoiceUploadModal: false,
      openClaimQuotationUploadModal: false,
      openClaimCancelModal: false,
      openFraudSuspicionModal: false,
      openClaimUpdateEstalyManagerNotesModal: false,
      openPauseClaimModal: false,
      openUpdateClaimTypeModal: false,
      openIbanUploadModal: false,
      openAiAnalysisFlow: false,
      openClaimChat: false,
      claimQuotations: [],
      claimInvoices: [],
    };
  },

  async beforeMount() {
    await this.fetchClaim();
  },

  methods: {
    ...mapActions("claims", ["fetch", "exportClaimData", "resetHandlingType", "reopen", "updateTicketStatus"]),
    ...mapActions("claimQuotations", { fetchClaimQuotations: "fetchIndex" }),
    ...mapActions("claimInvoices", { fetchClaimInvoices: "fetchIndex" }),
    ...mapMutations("claimDeclarations", ["resetIndex"]),

    formatPrice,

    async fetchClaim() {
      this.resetIndex();
      this.openAiAnalysisFlow = false;
      this.openClaimChat = false;
      await this.fetch(this.claimFriendlyId);
      await this.loadClaimQuotations();
      await this.loadClaimInvoices();
    },

    async reopenClaim() {
      if (!confirm("Retourner au dernier status avant la clotûre du sinistre ?")) return;

      await this.reopen();
    },

    async pauseClaim({ claim, reopeningDate }) {
      await this.updateTicketStatus({ claim: claim, reopeningDate: reopeningDate, ticketStatus: "paused" });
      this.openPauseClaimModal = false;
    },

    async onQuotationModalClose() {
      this.openClaimQuotationUploadModal = false;
      await this.loadClaimQuotations();
    },

    async onInvoiceModalClose() {
      this.openClaimInvoiceUploadModal = false;
      await this.loadClaimInvoices();
    },

    async loadClaimQuotations() {
      if (!this.currentClaim?.id) {
        this.claimQuotations = [];
        return;
      }

      const data = await this.fetchClaimQuotations({ claimId: this.currentClaim.id });
      this.claimQuotations = data?.claimQuotations ?? [];
    },

    async loadClaimInvoices() {
      if (!this.currentClaim?.id) {
        this.claimInvoices = [];
        return;
      }

      const data = await this.fetchClaimInvoices({ claimId: this.currentClaim.id });
      this.claimInvoices = data?.claimInvoices ?? [];
    },
  },

  watch: {
    async claimFriendlyId(to) {
      if (to) await this.fetchClaim();
    },
  },
};
</script>

<i18n src="./ClaimDetails.i18n.json"></i18n>
